import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, Users, TrendingUp, Clock, Plus, ArrowRight } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch stats and events in parallel
      const [statsRes, eventsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/stats`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/admin/events?include_past=false`, { credentials: 'include' })
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      if (eventsRes.ok) {
        const eventsData = await eventsRes.json();
        setUpcomingEvents(eventsData.slice(0, 5));
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Événements à venir',
      value: stats?.upcoming_events || 0,
      icon: Calendar,
      color: 'bg-[#db3b17]'
    },
    {
      title: 'Total inscriptions',
      value: stats?.total_registrations || 0,
      icon: Users,
      color: 'bg-[#48b4ff]'
    },
    {
      title: 'Ce mois-ci',
      value: stats?.events_this_month || 0,
      icon: TrendingUp,
      color: 'bg-[#ffd451]'
    }
  ];

  return (
    <AdminLayout title="Tableau de bord">
      <div className="space-y-8">
        {/* Welcome message */}
        <div className="bg-gradient-to-br from-[#FFF8E1] to-[#FFFDF7] rounded-2xl p-6 md:p-8 border border-[#E5E0D8]">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#2D2A26] mb-2">
            Bienvenue dans votre espace admin 👋
          </h2>
          <p className="text-[#645E55] mb-4">
            Gérez vos événements et suivez les inscriptions en temps réel.
          </p>
          <Link to="/admin/evenements/nouveau">
            <Button className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Créer un événement
            </Button>
          </Link>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {statCards.map((stat, index) => (
            <Card key={index} className="stats-card" data-testid={`stat-card-${index}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-[#645E55] mb-1">{stat.title}</p>
                    <p className="text-4xl font-bold text-[#2D2A26]">
                      {loading ? '-' : stat.value}
                    </p>
                  </div>
                  <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Registrations by type */}
        {stats?.registrations_by_type && Object.keys(stats.registrations_by_type).length > 0 && (
          <Card className="border-2 border-[#E5E0D8] rounded-2xl">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-[#2D2A26]">
                Inscriptions par type d'événement
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                {Object.entries(stats.registrations_by_type).map(([type, count]) => (
                  <div key={type} className="bg-[#FFF8E1] rounded-xl px-4 py-3">
                    <p className="text-sm text-[#645E55] capitalize">{type}</p>
                    <p className="text-2xl font-bold text-[#2D2A26]">{count}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Upcoming events */}
        <Card className="border-2 border-[#E5E0D8] rounded-2xl">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg text-[#2D2A26]">
              Prochains événements
            </CardTitle>
            <Link to="/admin/evenements">
              <Button variant="ghost" className="text-[#db3b17] hover:text-[#b92b0c]">
                Voir tout
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="w-12 h-12 skeleton rounded-xl" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-1/2 skeleton rounded" />
                      <div className="h-3 w-1/4 skeleton rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : upcomingEvents.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-4xl mb-3">📅</div>
                <p className="text-[#645E55]">Aucun événement à venir</p>
                <Link to="/admin/evenements/nouveau">
                  <Button className="mt-4 btn-secondary">
                    <Plus className="w-4 h-4 mr-2" />
                    Créer un événement
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {upcomingEvents.map((event) => {
                  const eventDate = new Date(event.date);
                  return (
                    <Link
                      key={event.event_id}
                      to={`/admin/evenements/${event.event_id}`}
                      className="flex items-center gap-4 p-4 rounded-xl hover:bg-[#FFF8E1] transition-colors"
                      data-testid={`upcoming-event-${event.event_id}`}
                    >
                      <div className="w-12 h-12 bg-[#db3b17] rounded-xl flex flex-col items-center justify-center text-white">
                        <span className="text-xs uppercase">{format(eventDate, 'MMM', { locale: fr })}</span>
                        <span className="text-lg font-bold">{format(eventDate, 'd')}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-[#2D2A26] truncate">{event.name}</h4>
                        <p className="text-sm text-[#645E55]">
                          {format(eventDate, "HH'h'mm", { locale: fr })} · {event.current_participants}/{event.max_participants} inscrits
                        </p>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                        event.is_cancelled 
                          ? 'bg-red-100 text-red-700'
                          : event.is_full 
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-green-100 text-green-700'
                      }`}>
                        {event.is_cancelled ? 'Annulé' : event.is_full ? 'Complet' : 'Ouvert'}
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
